import { BaseDAO } from './baseDAO';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ConstantMan } from '../constantMan';
import { ApiHelper } from '../apiHelper';
import { User } from 'anchor-shared/lib/model/entity/user';
import { HttpHeaders } from '@angular/common/http';
import { UserSearchResult } from 'anchor-shared/lib/model/interface/userSearchResult';
import { AdminUserGrid } from '../../../app/portal/admin/users/user.component';
import {
  AsyncValidatorResult,
  ListGridAsyncValidator,
} from '../../../app/shared/listGrid/listGrid.component';

@Injectable()
export class UserDAO extends BaseDAO<User> implements ListGridAsyncValidator {
  static PATHS = {
    FORGOT_PASSWORD: '/forgot-password/',
    RESET_PASSWORD: '/reset-password/',
    REQUEST_PHONE_VERIFY: '/request-phone-verify',
    CONFIRM_PHONE: '/confirm-phone',
    CONFIRM_EMAIL: '/confirm-email',
    CHECK_BACKGROUND: '/backgroundCheck',
    ACCOUNT_DELETION: '/deletion-request',
    ADMIN_ACCOUNT_DELETION: '/admin-deletion-request',
  };

  private currentUser: User;

  constructor(public readonly apiHelper: ApiHelper) {
    super(apiHelper, ConstantMan.API.RESOURCE.USER);
  }

  public getCurrentUser(): User {
    return this.currentUser;
  }

  public removeCurrentUser(): void {
    this.currentUser = null;
  }

  public setCurrentUser(currentUser: User): void {
    this.currentUser = currentUser;
  }

  public deletionRequest(): Observable<User> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      UserDAO.PATHS.ACCOUNT_DELETION;
    let method = 'PUT';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<User>(url, method, null, null, headers, null);
  }

  public adminDeletionRequest(userId: string | number): Observable<User> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      UserDAO.PATHS.ADMIN_ACCOUNT_DELETION;
    let method = 'PUT';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    let data = {
      id: userId,
    };
    return this.apiHelper.apiCall<User>(url, method, null, data, headers, null);
  }

  public requestPhoneVerify(): Observable<any> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      UserDAO.PATHS.REQUEST_PHONE_VERIFY;
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall<any>(url, method, null, {}, headers);
  }

  public sendSmsForPhoneVerification(
    userId: number,
    guestPhoneNumber?: string,
  ): Observable<any> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      `/sms/phone-verification/${userId}`;
    const data: any = {
      userId: userId,
    };
    if (guestPhoneNumber) {
      data.guestPhoneNumber = guestPhoneNumber;
    }
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall<any>(url, method, null, data, headers);
  }

  public adminUpdateUser(user: User, roleIds: number[]): Observable<User> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/admin/update/' +
      user.id;
    let method = 'PUT';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    let data: Object = user;
    data['roleIds'] = roleIds;

    let httpRequest: Observable<User> = this.apiHelper.apiCall<User>(
      url,
      method,
      null,
      data,
      headers,
      null,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (responseData: User) => {
          observer.next(responseData);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public updateUser(user: User): Observable<User> {
    let url: string = this.apiHelper.getServiceName() + this.getResource();
    let method = 'PUT';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    let data: Object = user;

    let httpRequest: Observable<User> = this.apiHelper.apiCall<User>(
      url,
      method,
      null,
      data,
      headers,
      null,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (responseData: User) => {
          observer.next(responseData);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public confirmPhone(code: string): Observable<any> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      UserDAO.PATHS.CONFIRM_PHONE;
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    let data = {
      code: code,
    };

    return this.apiHelper.apiCall<any>(url, method, null, data, headers);
  }

  public smsConfirmPhone(code: string, userId: number): Observable<any> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/sms/confirm-phone';
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    let data = {
      code: code,
      userId: userId,
    };

    return this.apiHelper.apiCall<any>(url, method, null, data, headers);
  }

  public confirmEmail<T>(
    confirmationToken: string,
    email: string,
  ): Observable<T> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      UserDAO.PATHS.CONFIRM_EMAIL;
    let data = {
      confirmationToken: confirmationToken,
      email: email,
    };

    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall<T>(url, method, null, data, headers);
  }

  public confirmEnrollment<T>(
    confirmationToken: string,
    email: string,
  ): Observable<T> {
    let url = `${this.apiHelper.getServiceName()}${this.getResource()}/confirm-enrollment/`;
    let data = {
      confirmationToken: confirmationToken,
      email: email,
    };

    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall<T>(url, method, null, data, headers);
  }

  /* Targets open enrollment endpoint */
  public enrollUser(user: Partial<User>): Observable<User> {
    let url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/';
    let userData = user as any;

    return this.newUserCall(url, userData);
  }

  public enrollCrewUser(user: Partial<User>): Observable<User> {
    let url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/crew/create';
    let userData = user as any;

    return this.newUserCall(url, userData);
  }

  public createBrokerAsAdmin(user: User): Observable<User> {
    let url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/broker/create';
    let userData = user as any;

    return this.newUserCall(url, userData);
  }

  findUserByGoogleId(googleId: string): Observable<any> {
    const url =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/google-id/' +
      googleId;
    const body = {};
    const method = 'GET';
    const httpHeader: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall(url, method, null, body, httpHeader);
  }

  findUserById(userId: number): Observable<any> {
    const url =
      this.apiHelper.getServiceName() + this.getResource() + '/' + userId;
    const body = {};
    const method = 'GET';
    const httpHeader: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall(url, method, null, body, httpHeader);
  }

  findUserWithStripeDetailsById(userId: number): Observable<any> {
    const url =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/payment-details/' +
      userId;
    const body = {};
    const method = 'GET';
    const httpHeader: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall(url, method, null, body, httpHeader);
  }

  findUserWithDetailsById(userId: number): Observable<any> {
    const url =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/user-details/' +
      userId;
    const body = {};
    const method = 'GET';
    const httpHeader: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall(url, method, null, body, httpHeader);
  }

  private newUserCall(url: string, data: any): Observable<User> {
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall<User>(url, method, null, data, headers);
  }

  public requestEmail(
    email: string,
    currentUrl: string,
    redirectPath?: string,
  ): Observable<User> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      UserDAO.PATHS.FORGOT_PASSWORD;
    let method = 'POST';
    let data: Object = {
      email: email,
      url: currentUrl,
      redirectPath: redirectPath,
    };

    return this.apiHelper.apiCall(url, method, null, data, null);
  }

  public getResettingUser(resetPasswordToken: string): Observable<User> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      UserDAO.PATHS.RESET_PASSWORD +
      resetPasswordToken;
    let method = 'GET';

    let httpRequest = this.apiHelper.apiCall(url, method, null, null, null);

    return Observable.create(function subscribe(observer) {
      httpRequest.subscribe(
        (data) => {
          this.userResettingPassword = data;
          observer.next(data);
          observer.complete();
        },
        (error) => {
          console.error(error);
        },
      );
    });
  }

  public checkEmail<T>(
    desiredEmail: string,
    currentUserId?: number,
  ): Observable<T> {
    let url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/';
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    let dataBody = {
      name: desiredEmail,
      currentUserId: currentUserId,
    };

    return this.apiHelper.apiCall<T>(
      url,
      method,
      null,
      dataBody,
      headers,
      'checkname',
      false,
    );
  }

  public updateAndLogin(
    id: number,
    resetPasswordToken: string,
    password: string,
  ): Observable<User> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      UserDAO.PATHS.RESET_PASSWORD +
      resetPasswordToken;
    let method = 'PUT';
    let data: Object = {
      id: id,
      password: password,
    };

    let httpRequest = this.apiHelper.apiCall(url, method, null, data, null);

    return Observable.create(function subscribe(observer) {
      httpRequest.subscribe(
        (responseData) => {
          observer.next(responseData);
          observer.complete();
        },
        (error) => {
          console.error(error);
        },
      );
    });
  }

  public checkBackground(id: number) {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      `/${id}` +
      UserDAO.PATHS.CHECK_BACKGROUND;
    let method = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    let httpRequest = this.apiHelper.apiCall(url, method, null, null, headers);

    return Observable.create(function subscribe(observer) {
      httpRequest.subscribe(
        (data) => {
          observer.next(data);
          observer.complete();
        },
        (error) => {
          console.error(error);
        },
      );
    });
  }

  public getUsersForPayouts(): Observable<User[]> {
    let url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/payouts';
    let method = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall(url, method, null, null, headers);
  }

  public loadPayoutUsers(): Observable<User[]> {
    let url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/payouts';
    let method = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall(url, method, null, null, headers);
  }

  public createEmailAccountForNewCustomer(
    user: Partial<User>,
  ): Observable<User> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/customer/email/create';
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<User>(url, method, null, user, headers);
  }

  public updateFromPartial(id: number, user: Partial<User>): Observable<User> {
    const url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/partial/' + id;
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<User>(url, method, null, user, headers);
  }

  public adminUpdateFromPartial(
    id: number,
    user: Partial<User>,
  ): Observable<User> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/admin/partial/' +
      id;
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<User>(url, method, null, user, headers);
  }

  adminUserSearch(searchText: string): Observable<UserSearchResult[]> {
    const url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/admin/search';
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      searchText: searchText,
    };
    return this.apiHelper.apiCall<UserSearchResult[]>(
      url,
      method,
      null,
      data,
      headers,
    );
  }

  currentUserIsAdmin(): boolean {
    if (this.currentUser) {
      for (let role of this.currentUser.roles) {
        if (role.adminAccess) {
          return true;
        }
      }
    }
    return false;
  }

  currentUserIs(roleName: string): boolean {
    return (
      this.currentUser?.roles.some((role) => role.type === roleName) ?? false
    );
  }

  checkForUserId(userEmail: any): Observable<UserSearchResult> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/checkForUserId/' +
      userEmail;
    let method = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<UserSearchResult>(
      url,
      method,
      null,
      userEmail,
      headers,
    );
  }

  addRoleToExistingUser(roleType: string, userId: number): any {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/addRoleToExistingUser';
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data: Object = {
      roleType: roleType,
      userId: userId,
    };
    return this.apiHelper.apiCall<UserSearchResult>(
      url,
      method,
      null,
      data,
      headers,
    );
  }

  public loadAdminGridUsers(
    page: number,
    searchQuery?: string,
    sortBy?: string,
    sortOrder?: string,
  ): Observable<{ count: number; rows: AdminUserGrid[] }> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/admin/grid/' +
      `?page=${page}` +
      `${searchQuery ? '&searchQuery=' + searchQuery : ''}` +
      `${sortBy ? '&sortBy=' + sortBy : ''}` +
      `${sortOrder ? '&sortOrder=' + sortOrder : ''}`;
    let method = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall(url, method, null, null, headers);
  }

  runSearch(
    page: number,
    searchQuery?: string,
    sortBy?: string,
    sortOrder?: string,
  ): Observable<AsyncValidatorResult> {
    return this.loadAdminGridUsers(page, searchQuery, sortBy, sortOrder);
  }

  integrateStripe(userId: number, tempAuthCode: string): Observable<boolean> {
    const url =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/' +
      userId +
      '/stripe-complete';
    const method = 'POST';
    const headers = this.apiHelper.getDefaultHeader();
    const data = { code: tempAuthCode };

    return this.apiHelper.apiCall<boolean>(url, method, null, data, headers);
  }

  public getExpressDashboardLoginLink(
    userId: number,
  ): Observable<{ link: string }> {
    const url =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/user/express-dashboard/';
    const method = 'POST';
    const headers = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall<{ link: string }>(
      url,
      method,
      null,
      null,
      headers,
    );
  }
}
